import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Share2, Pencil } from 'lucide-react';
import api from '@/lib/api';
import { usePoll, usePollResults } from '../hooks';
import { PollStatusBadge, PollQuestionView, PollResultsView } from '../components';
import type { PollAnswer } from '../types';

export function PollPage() {
  const { pollId } = useParams<{ pollId: string }>();
  const navigate = useNavigate();
  const { poll, loading, error, refresh } = usePoll(pollId);
  const {
    results,
    loading: resultsLoading,
    error: resultsError,
    refresh: refreshResults,
  } = usePollResults(
    poll && (poll.isCreator || (poll.hasResponded && poll.resultsVisibility === 'public'))
      ? pollId
      : undefined,
  );

  const [answers, setAnswers] = useState<Record<string, PollAnswer>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !poll) {
    return (
      <div className="max-w-3xl mx-auto">
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Poll not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canVote = poll.status === 'active' && !poll.hasResponded && !poll.isCreator;
  const showResults = poll.isCreator || (poll.hasResponded && poll.resultsVisibility === 'public');

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    const answersList = poll.questions.map((q) => {
      const a = answers[q.questionId];
      if (!a) {
        return { questionId: q.questionId, selectedOptions: [], textAnswer: '' };
      }
      return a;
    });

    try {
      await api.submitPollResponse(pollId!, { answers: answersList });
      refresh();
      refreshResults();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (action: 'publish' | 'close') => {
    setActionError(null);
    try {
      if (action === 'publish') {
        await api.publishPoll(pollId!);
      } else {
        await api.closePoll(pollId!);
      }
      refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `Failed to ${action} poll`);
    }
  };

  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/a/arche_polls/${poll.pollId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text from a temporary input
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-bold">{poll.title}</h1>
          <PollStatusBadge status={poll.status} />
        </div>
        {poll.description && <p className="text-muted-foreground">{poll.description}</p>}
        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
          <span>
            {poll.responseCount} {poll.responseCount === 1 ? 'response' : 'responses'}
          </span>
          {poll.closesAt && (
            <span>
              {new Date(poll.closesAt) > new Date() ? 'Closes' : 'Closed'}{' '}
              {new Date(poll.closesAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {actionError && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
          {actionError}
        </div>
      )}

      {/* Creator actions */}
      {poll.isCreator && (
        <div className="flex items-center gap-2 mb-6">
          {poll.status === 'draft' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/a/arche_polls/${poll.pollId}/edit`)}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button size="sm" onClick={() => handleAction('publish')}>
                Publish
              </Button>
            </>
          )}
          {poll.status === 'active' && (
            <>
              <Button variant="outline" size="sm" onClick={handleCopyLink}>
                <Share2 className="h-4 w-4 mr-1" />
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleAction('close')}>
                Close Poll
              </Button>
            </>
          )}
        </div>
      )}

      {/* Voting form */}
      {canVote && (
        <div className="space-y-6 mb-8">
          {poll.questions.map((q) => (
            <PollQuestionView
              key={q.questionId}
              question={q}
              answer={answers[q.questionId] || { questionId: q.questionId }}
              onChange={(a) => setAnswers((prev) => ({ ...prev, [q.questionId]: a }))}
            />
          ))}

          {submitError && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              {submitError}
            </div>
          )}

          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Response'}
          </Button>
        </div>
      )}

      {/* Already responded message */}
      {poll.hasResponded && !poll.isCreator && poll.resultsVisibility === 'creator_only' && (
        <div className="mb-6 p-4 bg-muted rounded-md">
          <p className="text-sm text-muted-foreground">
            You have already responded to this poll. Results are only visible to the poll creator.
          </p>
        </div>
      )}

      {/* Results */}
      {showResults && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-4">Results</h2>
          {resultsLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : resultsError ? (
            <p className="text-destructive text-sm">{resultsError}</p>
          ) : results ? (
            <PollResultsView results={results} />
          ) : null}
        </div>
      )}

      {/* Draft questions preview */}
      {poll.isCreator && poll.status === 'draft' && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-4">Questions Preview</h2>
          <div className="space-y-4">
            {poll.questions.map((q, i) => (
              <Card key={q.questionId}>
                <CardContent className="pt-4">
                  <p className="font-medium">
                    {i + 1}. {q.text}
                    {q.required && <span className="text-destructive ml-1">*</span>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Type: {q.type.replace('_', ' ')}
                  </p>
                  {q.options && (
                    <ul className="mt-2 space-y-1">
                      {q.options.map((opt, j) => (
                        <li key={j} className="text-sm text-muted-foreground">
                          - {opt}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
